import React, { useEffect, useState, useCallback } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import {
  listCompanyTimeYears, getCompanyTimeYear,
  createCompanyTimeYear,
  copyCompanyTimeYear,
  getHolidayProposals, bulkSetCantonalHolidays,
  addManualHoliday, deleteHoliday, updateHoliday,
  updateCompanyTimeYear, upsertMonth,
  updateTenantCanton,
  ROMAND_CANTONS, MONTH_LABELS,
  WEEKDAY_LABELS_FR, HOLIDAY_KINDS, KIND_BY_VALUE,
} from '../../api/hrTime';
import PageHeader from '../../components/PageHeader';
import InfoTooltip from '../../components/InfoTooltip';
import { formatDate, formatNumber } from '../../utils/format';
import './HrPage.css';


// Convertit "YYYY-MM-DD" ou Date → "LU 25-05-2026"
function formatDateWithDay(value) {
  if (!value) return '';
  let d;
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    else d = new Date(value);
  } else {
    d = value;
  }
  if (isNaN(d?.getTime?.())) return '';
  // getUTCDay : 0=Dim … 6=Sam → re-mapper en LU=0…DI=6
  const utcDay = d.getUTCDay();
  const idx = (utcDay + 6) % 7;
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${WEEKDAY_LABELS_FR[idx]} ${dd}-${mm}-${d.getUTCFullYear()}`;
}


/* ════════════════════════════════════════════════════════════════════════
   Page racine — onglet Time → Temps entreprise
   ════════════════════════════════════════════════════════════════════════ */

export default function TimeCompanyPage({ activeTenant, onTenantUpdated, year: yearProp, onYearChange, embedded }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const cantonCode = activeTenant?.canton_code || null;

  const [years, setYears] = useState([]);
  // Année active : priorité au prop global (persisté App), fallback local.
  const [localYear, setLocalYear] = useState(yearProp ?? null);
  const selectedYear = yearProp ?? localYear;
  const setSelectedYear = (y) => {
    setLocalYear(y);
    if (onYearChange && y != null) onYearChange(y);
  };
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* ── data fetch ─────────────────────────────────────────────────────── */
  const refreshYears = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    const r = await listCompanyTimeYears(tenantId);
    setLoading(false);
    if (r.error) { setError(r.error); return; }
    const arr = r.data || [];
    setYears(arr);
    if (arr.length > 0 && (selectedYear == null || !arr.some(y => y.year === selectedYear))) {
      setSelectedYear(arr[0].year);
    } else if (arr.length === 0) {
      setSelectedYear(null);
      setDetail(null);
    }
  }, [tenantId, selectedYear]); // eslint-disable-line

  // silent = rafraîchit les données SANS basculer `loading` (donc sans démonter
  // la card) : les éditions en place (catégorie d'un férié, valeurs d'un mois)
  // mettent à jour le contenu sans faire sauter le scroll en haut de page.
  const refreshDetail = useCallback(async (silent = false) => {
    if (!tenantId || selectedYear == null) { setDetail(null); return; }
    if (!silent) setLoading(true);
    const r = await getCompanyTimeYear(tenantId, selectedYear);
    if (!silent) setLoading(false);
    if (r.error) {
      // 404 = année simplement non créée → état vide, pas une erreur.
      setError(r.status === 404 ? null : r.error);
      setDetail(null);
      return;
    }
    setDetail(r.data);
    setError(null);
  }, [tenantId, selectedYear]);

  useEffect(() => { refreshYears(); }, [tenantId]);   // eslint-disable-line
  useEffect(() => { refreshDetail(); }, [tenantId, selectedYear]);   // eslint-disable-line

  /* ── actions ────────────────────────────────────────────────────────── */
  async function handleSetCanton(code) {
    const r = await updateTenantCanton(tenantId, code || null);
    if (r.error) setError(r.error);
    else if (onTenantUpdated) onTenantUpdated(r.data);
  }

  async function handleCreateYear(year, weekly) {
    setError(null);
    const r = await createCompanyTimeYear(tenantId, { year, weekly_contractual_hours: String(weekly) });
    if (r.error) { setError(r.error); return false; }
    if (cantonCode) {
      const props = await getHolidayProposals(cantonCode, year);
      if (!props.error) {
        const items = (props.data.items || [])
          .filter(p => p.is_default_checked)
          .map(p => ({ holiday_date: p.holiday_date, label: p.label }));
        if (items.length > 0) await bulkSetCantonalHolidays(tenantId, year, items);
      }
    }
    await refreshYears();
    setSelectedYear(year);
    await refreshDetail();  // l'année créée = année déjà sélectionnée → recharge forcée
    return true;
  }

  async function handleCopyYear(fromYear, toYear, overwrite) {
    const r = await copyCompanyTimeYear(tenantId, fromYear, toYear, overwrite);
    if (r.error) { setError(r.error); return false; }
    setError(null);
    await refreshYears();
    setSelectedYear(toYear);
    await refreshDetail();  // recharge forcée si toYear == année sélectionnée
    return true;
  }

  async function handleUpdateWeekly(value) {
    if (selectedYear == null) return;
    const r = await updateCompanyTimeYear(tenantId, selectedYear, { weekly_contractual_hours: String(value) });
    if (r.error) { setError(r.error); return; }
    setError(null);
    await refreshDetail();
  }

  async function handleUpdateVacation(value) {
    if (selectedYear == null) return;
    const r = await updateCompanyTimeYear(tenantId, selectedYear, { vacation_days_full_time: String(value) });
    if (r.error) { setError(r.error); return; }
    setError(null);
    await refreshDetail();
  }

  async function handleReloadCantonalHolidays() {
    if (!cantonCode || selectedYear == null) return;
    if (!window.confirm(t('phrases:p_5abd5abb33', { canton: cantonCode, year: selectedYear }))) return;
    const props = await getHolidayProposals(cantonCode, selectedYear);
    if (props.error) { setError(props.error); return; }
    const items = (props.data.items || [])
      .filter(p => p.is_default_checked)
      .map(p => ({ holiday_date: p.holiday_date, label: p.label }));
    const r = await bulkSetCantonalHolidays(tenantId, selectedYear, items);
    if (r.error) { setError(r.error); return; }
    setError(null);
    await refreshDetail();
  }

  async function handleDeleteHoliday(hid) {
    const r = await deleteHoliday(tenantId, hid);
    if (r.error) { setError(r.error); return; }
    setError(null);
    await refreshDetail();
  }

  async function handleUpdateHoliday(hid, patch) {
    const r = await updateHoliday(tenantId, hid, patch);
    if (r.error) { setError(r.error); return false; }
    setError(null);
    await refreshDetail(true);   // silencieux : ne fait pas sauter le scroll
    return true;
  }

  async function handleAddManualHoliday(dateStr, label, kind) {
    const r = await addManualHoliday(tenantId, selectedYear, {
      holiday_date: dateStr, label, kind,
    });
    if (r.error) { setError(r.error); return false; }
    setError(null);
    await refreshDetail();
    return true;
  }

  async function handleUpsertMonth(month, payload) {
    const r = await upsertMonth(tenantId, selectedYear, month, payload);
    if (r.error) { setError(r.error); return false; }
    setError(null);
    await refreshDetail(true);   // silencieux : ne fait pas sauter le scroll
    return true;
  }

  /* ── render ─────────────────────────────────────────────────────────── */
  if (!tenantId) return <div style={{ padding: 24 }}>{p('Sélectionnez un tenant.')}</div>;

  return (
    <div style={{ padding: embedded ? 0 : '16px 24px', maxWidth: 1180 }}>
      {!embedded && <PageHeader title={p('Calendrier d\'entreprise')} />}
      <Toolbar
        tenantId={tenantId}
        cantonCode={cantonCode}
        years={years}
        onSetCanton={handleSetCanton}
        onCreateYear={handleCreateYear}
        onCopyYear={handleCopyYear}
      />

      {error && <ErrorBanner msg={error} onDismiss={() => setError(null)} />}

      {loading && <div style={{ padding: 12, color: '#888' }}>{t('common:status.loading')}</div>}

      {!loading && detail && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20, marginTop: 16 }}>
            <SynthesisCard
              detail={detail}
              onUpdateWeekly={handleUpdateWeekly}
              onUpdateVacation={handleUpdateVacation}
            />
            <HolidaysCard
              holidays={detail.holidays}
              cantonCode={cantonCode}
              year={selectedYear}
              onDelete={handleDeleteHoliday}
              onReloadCantonal={cantonCode ? handleReloadCantonalHolidays : null}
              onAddManual={handleAddManualHoliday}
              onUpdate={handleUpdateHoliday}
            />
          </div>
          <MonthlyTable
            months={detail.computed.months}
            totals={detail.computed}
            vacationDays={detail.params.vacation_days_full_time}
            onUpsert={handleUpsertMonth}
          />
        </>
      )}

      {!loading && years.length === 0 && (
        <div style={{ padding: 24, color: '#888', fontStyle: 'italic' }}>
          {p('Aucune année saisie. Crée la première année avec « + Nouvelle ».')}
        </div>
      )}

      {!loading && !detail && years.length > 0 && selectedYear != null && (
        <div style={{ padding: 24, color: '#888', fontStyle: 'italic' }}>
          {p('Calendrier ')}{selectedYear}{p(' non créé pour ce tenant.')}
          {' '}{p('Utilise « + Nouvelle » ou « Copier… » pour le créer.')}
        </div>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Header + Toolbar
   ════════════════════════════════════════════════════════════════════════ */

function Toolbar({
  tenantId, cantonCode, years,
  onSetCanton, onCreateYear, onCopyYear,
}) {
  const p = useP();
  const [showCreate, setShowCreate] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  return (
    <div style={{
      display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
      padding: '8px 0', borderBottom: '1px solid #e3e8ef',
      fontSize: 13,
    }}>
      <Label>{p('Canton')}</Label>
      <select value={cantonCode || ''} onChange={e => onSetCanton(e.target.value || null)}
              style={{ padding: '3px 6px' }}>
        <option value="">—</option>
        {ROMAND_CANTONS.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
      </select>

      <Separator />

      <button onClick={() => setShowCreate(true)} style={btnStyle}>{p('+ Nouvelle')}</button>
      {years.length > 0 && (
        <button onClick={() => setShowCopy(true)} style={btnStyle}>{p('Copier…')}</button>
      )}

      {showCreate && <CreateYearModal cantonCode={cantonCode}
                                       onClose={() => setShowCreate(false)}
                                       onSubmit={async (y, w) => {
                                         if (await onCreateYear(y, w)) setShowCreate(false);
                                       }} />}
      {showCopy && <CopyYearModal years={years}
                                   onClose={() => setShowCopy(false)}
                                   onSubmit={async (f, t, ow) => {
                                     if (await onCopyYear(f, t, ow)) setShowCopy(false);
                                   }} />}
    </div>
  );
}


function Label({ children }) {
  return <span style={{ color: '#666', fontWeight: 500 }}>{children} :</span>;
}
function Separator() {
  return <span style={{ color: '#cdd5df' }}>|</span>;
}
const btnStyle = {
  padding: '4px 10px', fontSize: 13, cursor: 'pointer',
  background: '#fff', border: '1px solid #cdd5df', borderRadius: 4,
};


/* ════════════════════════════════════════════════════════════════════════
   Synthèse (fusion paramètres + valeurs calculées)
   ════════════════════════════════════════════════════════════════════════ */

function SynthesisCard({ detail, onUpdateWeekly, onUpdateVacation }) {
    const { t } = useTranslation();
  const p = useP();
const { params, computed } = detail;
  const [weekly, setWeekly] = useState(params.weekly_contractual_hours);
  useEffect(() => { setWeekly(params.weekly_contractual_hours); }, [params.id, params.weekly_contractual_hours]);
  const [vacationDays, setVacationDays] = useState(params.vacation_days_full_time);
  useEffect(() => { setVacationDays(params.vacation_days_full_time); }, [params.id, params.vacation_days_full_time]);

  function saveWeekly() {
    if (String(weekly) !== String(params.weekly_contractual_hours)) onUpdateWeekly(weekly);
  }
  function saveVacation() {
    if (String(vacationDays) !== String(params.vacation_days_full_time)) onUpdateVacation(vacationDays);
  }

  // Convention suisse : t0_annual_hours backend = heures dues annuelles
  // (invariant aux rattrapages). h/jour avec rattrapage = t0 ÷ jours travaillés net.
  const joursDus    = Number(computed.working_days_year) - Number(computed.weekday_offered_year);
  const heuresDues  = Number(computed.t0_annual_hours);
  const joursTravNet = Number(computed.final_working_days_year);
  const hParJourAvecRattrapage = joursTravNet > 0 ? heuresDues / joursTravNet : Number(computed.daily_contractual_hours);
  // Standard vacation/OT — informatif (TT inchangé) : politique vacances @100%
  // × h/jour standard ; OT standard = TT − vacances standard.
  const stdVacationHours = Number(vacationDays || 0) * Number(computed.daily_contractual_hours);
  const stdOtHours = heuresDues - stdVacationHours;

  return (
    <section style={cardStyle}>
      <h3 style={cardTitleStyle}>{t('phrases:p_ab761b3579')}</h3>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f7f9fc', textAlign: 'left' }}>
            <th style={thStyle}>Indicateur</th>
            <th style={thRight}>{p('Valeur')}</th>
          </tr>
        </thead>
        <tbody>
          <tr style={rowStyle}>
            <td style={lblCellStyle}>
              Heures contractuelles / sem.
              <span style={{ marginLeft: 6, color: '#9aa3ad', fontSize: 11 }}>{p('(décimal)')}</span>
            </td>
            <td style={valCellStyle}>
              <input
                type="number" min="1" max="60" step="0.5"
                value={weekly}
                onChange={(e) => setWeekly(e.target.value)}
                onBlur={saveWeekly}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                style={editableValueStyle}
              />
              <span style={unitStyle}>h</span>
            </td>
          </tr>
          <tr style={rowStyle}>
            <td style={lblCellStyle}>
              {p('Jours de vacances / an @100%')}
              <span style={{ marginLeft: 6, color: '#9aa3ad', fontSize: 11 }}>{p('(défaut contrats)')}</span>
            </td>
            <td style={valCellStyle}>
              <input
                type="number" min="0" max="50" step="0.5"
                value={vacationDays}
                onChange={(e) => setVacationDays(e.target.value)}
                onBlur={saveVacation}
                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                style={editableValueStyle}
              />
              <span style={unitStyle}>j</span>
            </td>
          </tr>
          <SynRow label={p('H. / jour standard')}
                  value={`${formatNumber(computed.daily_contractual_hours, 2)} h`} />
          <SynRow label={p('Jours semaine annuels')}
                  value={computed.weekday_count_year} />
          <SynRow label={t('phrases:p_829234990c')}
                  value={computed.weekday_holidays_year} />
          <SynRow label={t('phrases:p_f296a4c334')}
                  value={computed.working_days_year} />
          <SynRow label={t('phrases:p_6e5b435b25')}
                  value={computed.weekday_offered_year} />
          <SynRow label={p('Jours dus')}
                  value={formatNumber(joursDus, 1)} bold />
          <SynRow label={t('phrases:p_14cebd751a')}
                  value={formatNumber(computed.compensated_days_year, 1)} />
          <SynRow label={t('phrases:p_01e4656afc')}
                  value={formatNumber(joursTravNet, 1)} bold />
          <SynRow label={p('H. / jour avec rattrapage')}
                  value={`${formatNumber(hParJourAvecRattrapage, 2)} h`} />
          <SynRow label={t('phrases:p_618b52ec56')}
                  value={`${formatNumber(computed.tt_annual_hours, 2)} h`} />
          <SynRow label={p('Temps total (TT, h)')}
                  value={`${formatNumber(heuresDues, 2)} h`} bold highlight />
          <SynRow label={p('Temps de vacances standard (h)')}
                  value={`${formatNumber(stdVacationHours, 2)} h`} />
          <SynRow label={t('phrases:p_c2be968eda')}
                  value={`${formatNumber(stdOtHours, 2)} h`} bold />
        </tbody>
      </table>
    </section>
  );
}

function SynRow({ label, value, bold, highlight }) {
  return (
    <tr style={rowStyle}>
      <td style={lblCellStyle}>{label}</td>
      <td style={{
        ...valCellStyle,
        fontWeight: bold ? 600 : 400,
        color: highlight ? '#1f3864' : 'inherit',
      }}>
        {value}
      </td>
    </tr>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Fériés officiels
   ════════════════════════════════════════════════════════════════════════ */

function HolidaysCard({ holidays, cantonCode, year, onDelete, onReloadCantonal, onAddManual, onUpdate }) {
  const p = useP();
  const { t } = useTranslation();
  const [showAdd, setShowAdd] = useState(false);
  const sorted = [...holidays].sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));

  return (
    <section style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ ...cardTitleStyle, marginBottom: 0 }}>
          {p('Fériés et Congés')}{cantonCode && <span style={{ color: '#888', fontWeight: 400 }}>{t('phrases:p_0a7d700daf', { canton: cantonCode })}</span>}
        </h3>
        <div style={{ display: 'flex', gap: 6 }}>
          {onReloadCantonal && <button style={btnStyle} onClick={onReloadCantonal}>{p('Recharger')}</button>}
          <button style={btnStyle} onClick={() => setShowAdd(true)}>{p('+ Ajouter')}</button>
        </div>
      </div>
      {sorted.length === 0 ? (
        <div style={{ color: '#aaa', fontStyle: 'italic', fontSize: 13, padding: 12 }}>
          {p('Aucun jour saisi.')}{onReloadCantonal && p(' Clique sur « Recharger » pour importer les fériés cantonaux.')}
        </div>
      ) : (
        <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f7f9fc', textAlign: 'left' }}>
              <th style={thStyle}>{p('Date')}</th>
              <th style={thStyle}>{t('phrases:p_f5485bbae8')}</th>
              <th style={{ ...thStyle, width: 130 }}>{t('phrases:p_6b38300a25')}</th>
              <th style={{ ...thStyle, width: 70 }}>{p('Source')}</th>
              <th style={{ ...thStyle, width: 24 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(h => (
              <tr key={h.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ ...tdStyle, fontVariantNumeric: 'tabular-nums' }}>{formatDateWithDay(h.holiday_date)}</td>
                <td style={tdStyle}>{h.label}</td>
                <td style={tdStyle}>
                  <span
                    onClick={() => {
                      if (!onUpdate) return;
                      const i = HOLIDAY_KINDS.findIndex(k => k.value === h.kind);
                      const next = HOLIDAY_KINDS[(i + 1) % HOLIDAY_KINDS.length];
                      onUpdate(h.id, { kind: next.value });
                    }}
                    title={t('phrases:p_498c7ed16e')}
                    style={cycleValueStyle}
                  >
                    {(KIND_BY_VALUE[h.kind] || KIND_BY_VALUE.official).short}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: h.source === 'manual' ? '#c45d3e' : '#888' }}>
                  {h.source === 'manual' ? p('manuel') : 'cantonal'}
                </td>
                <td style={tdStyle}>
                  <button onClick={() => onDelete(h.id)} title={p('Retirer')}
                          style={{ color: '#c0392b', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showAdd && <AddHolidayModal year={year}
                                    onClose={() => setShowAdd(false)}
                                    onSubmit={async (d, l, k) => {
                                      if (await onAddManual(d, l, k)) setShowAdd(false);
                                    }} />}
    </section>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Tableau mensuel — avec rattrapages éditables + mémo popup
   ════════════════════════════════════════════════════════════════════════ */

function MonthlyTable({ months, totals, vacationDays, onUpsert }) {
  // Backend renvoie working_hours_net mensuel = jours travaillés × h/jour avec
  // rattrapage (constante annuelle), et t0_annual_hours = heures dues annuelles
  // (invariant aux rattrapages). On lit directement les valeurs backend.
  //
  // OT (temps ouvert) mensuel = TT − vacances, vacances réparties LINÉAIREMENT
  // (hypothèse) : vacances annuelles ÷ 12 chaque mois. Total = OT standard.
    const { t } = useTranslation();
  const p = useP();
const dailyH       = Number(totals.daily_contractual_hours) || 0;
  const vacHoursYear = Number(vacationDays || 0) * dailyH;
  const vacPerMonth  = vacHoursYear / 12;
  return (
    <section style={{ ...cardStyle, marginTop: 20 }}>
      <h3 style={cardTitleStyle}>{p('Tableau mensuel')}</h3>
      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f7f9fc' }}>
            <th style={thStyle}>{p('Mois')}</th>
            <th style={thRight}>{p('J. sem.')}</th>
            <th style={thRight}>{p('Fériés')}</th>
            <th style={thRight}>{p('J. ouvrés')}</th>
            <th style={thRight}>{p('Congés offerts')}</th>
            <th style={thRight}>{p('Congés rattrapés')}</th>
            <th style={thRight}>{p('J. travaillés')}</th>
            <th style={thRight}>{p('Temps total (TT, h)')}</th>
            <th style={thRight} title={p('Temps ouvert = TT − vacances (réparties linéairement, ÷ 12)')}>{p('TO (h)')}<InfoTooltip term="capacityTime" align="right" /></th>
            <th style={thRight} title={t('phrases:p_23105c4765')}>TO (h cum.)</th>
            <th style={{ ...thStyle, width: 24 }}></th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            let cum = 0;
            return [...months]
              .sort((a, b) => a.month - b.month)
              .map(m => {
                cum += Number(m.working_hours_net) - vacPerMonth;
                return (
                  <MonthRow key={m.month} m={m} vacPerMonth={vacPerMonth} cumTo={cum} onUpsert={onUpsert} />
                );
              });
          })()}
        </tbody>
        <tfoot>
          <tr style={{ background: '#f7f9fc', fontWeight: 600 }}>
            <td style={tdStyle}>{p('Total')}</td>
            <td style={tdRight}>{totals.weekday_count_year}</td>
            <td style={tdRight}>{totals.weekday_holidays_year}</td>
            <td style={tdRight}>{totals.working_days_year}</td>
            <td style={tdRight}>{totals.weekday_offered_year}</td>
            <td style={tdRight}>{formatNumber(totals.compensated_days_year, 1)}</td>
            <td style={tdRight}>{formatNumber(totals.final_working_days_year, 1)}</td>
            {/* TT annuel = heures dues backend (invariant aux rattrapages) */}
            <td style={{ ...tdRight, color: '#1f3864' }}>{formatNumber(totals.t0_annual_hours, 2)}</td>
            {/* TO annuel = TT − vacances (= OT standard) */}
            <td style={{ ...tdRight, color: '#1f3864' }}>{formatNumber(Number(totals.t0_annual_hours) - vacHoursYear, 2)}</td>
            {/* TO cumulé en fin d'année = TO annuel */}
            <td style={{ ...tdRight, color: '#1f3864' }}>{formatNumber(Number(totals.t0_annual_hours) - vacHoursYear, 2)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}


function MonthRow({ m, vacPerMonth = 0, cumTo = 0, onUpsert }) {
  const [showNote, setShowNote] = useState(false);
  const hasComp = Number(m.compensated_days_off) > 0;
  const hasOffered = Number(m.weekday_offered) > 0;
  const hasNote = !!m.note;

  return (
    <>
      <tr style={{ borderBottom: '1px solid #eee' }}>
        <td style={tdStyle}>{MONTH_LABELS[m.month - 1]}</td>
        <td style={tdRight}>{m.weekday_count}</td>
        <td style={tdRight}>{m.weekday_holidays}</td>
        <td style={tdRight}>{m.working_days}</td>
        <td style={tdRight}>{m.weekday_offered}</td>
        {/* Déduit des dates marquées « congé rattrapé » : rien à saisir ici. */}
        <td style={tdRight}>{formatNumber(m.compensated_days_off, 1)}</td>
        <td style={tdRight}>{formatNumber(m.working_days_net, 1)}</td>
        {/* TT mensuel backend = jours travaillés × h/jour avec rattrapage */}
        <td style={{ ...tdRight, color: (hasComp || hasOffered) ? '#1f3864' : 'inherit' }}>
          {formatNumber(m.working_hours_net, 2)}
        </td>
        {/* TO mensuel = TT − vacances linéaires (÷ 12) */}
        <td style={tdRight}>
          {formatNumber(Number(m.working_hours_net) - vacPerMonth, 2)}
        </td>
        {/* TO cumulé depuis janvier */}
        <td style={{ ...tdRight, color: '#1f3864' }}>
          {formatNumber(cumTo, 2)}
        </td>
        <td style={tdStyle}>
          <button
            onClick={() => setShowNote(true)}
            title={hasNote ? m.note : p('Ajouter un mémo')}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: hasNote ? '#1f3864' : '#bbb', padding: 0, fontSize: 14,
            }}>
            {hasNote ? '🗒' : '+'}
          </button>
        </td>
      </tr>
      {showNote && (
        <NoteModal
          month={m.month}
          initial={m.note || ''}
          onClose={() => setShowNote(false)}
          onSubmit={async (note) => {
            const ok = await onUpsert(m.month, { note: note || null });
            if (ok) setShowNote(false);
          }}
        />
      )}
    </>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   Modals
   ════════════════════════════════════════════════════════════════════════ */

function CreateYearModal({ onClose, onSubmit, cantonCode }) {
    const { t } = useTranslation();
  const p = useP();
const [year, setYear] = useState(new Date().getFullYear());
  const [weekly, setWeekly] = useState('40');
  return (
    <Modal title={t('phrases:p_39bcceafa7')} onClose={onClose}>
      <FormRow label={t('phrases:p_1f910d98d7')}>
        <input type="number" min="2000" max="2100" value={year}
               onChange={e => setYear(parseInt(e.target.value, 10))} style={modalInput} />
      </FormRow>
      <FormRow label={t('phrases:p_16a2be9d71')}>
        <input type="text" inputMode="decimal" value={weekly}
               onChange={e => setWeekly(e.target.value)}
               style={{ ...modalInput, ...editableInputStyle, width: 100 }} />
      </FormRow>
      <p style={modalHint}>
        {cantonCode
          ? t('phrases:p_e58d7d6611', { canton: cantonCode })
          : p("Définis d'abord le canton du tenant pour charger les fériés automatiquement.")}
      </p>
      <ModalActions>
        <button onClick={onClose} style={btnStyle}>{t('common:actions.cancel')}</button>
        <button onClick={() => onSubmit(year, weekly)} style={{ ...btnStyle, ...btnPrimary }}>{p('Créer')}</button>
      </ModalActions>
    </Modal>
  );
}


function CopyYearModal({ onClose, years, onSubmit }) {
  const { t } = useTranslation();
  const p = useP();
  const sourceYears = years.map(y => y.year);
  const [fromYear, setFromYear] = useState(sourceYears[0]);
  const [toYear, setToYear] = useState((sourceYears[0] || new Date().getFullYear()) + 1);
  const [overwrite, setOverwrite] = useState(false);
  const targetExists = years.some(y => y.year === toYear);
  return (
    <Modal title={t('phrases:p_82c280c4d1')} onClose={onClose}>
      <FormRow label={p('Depuis')}>
        <select value={fromYear} onChange={e => setFromYear(parseInt(e.target.value, 10))} style={modalInput}>
          {sourceYears.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </FormRow>
      <FormRow label={p('Vers')}>
        <input type="number" min="2000" max="2100" value={toYear}
               onChange={e => setToYear(parseInt(e.target.value, 10))} style={modalInput} />
      </FormRow>
      {targetExists && (
        <div style={{ background: '#fff3cd', padding: 8, borderRadius: 4, margin: '8px 0', fontSize: 13 }}>
          <label>
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} />
            {' '}{t('phrases:p_f143bcbc6f', { year: toYear })}
          </label>
        </div>
      )}
      <p style={modalHint}>{t('phrases:p_7425729fd8', { year: toYear })}</p>
      <ModalActions>
        <button onClick={onClose} style={btnStyle}>{t('common:actions.cancel')}</button>
        <button onClick={() => onSubmit(fromYear, toYear, overwrite)}
                disabled={fromYear === toYear || (targetExists && !overwrite)}
                style={{ ...btnStyle, ...btnPrimary }}>
          {p('Copier')}
        </button>
      </ModalActions>
    </Modal>
  );
}


function AddHolidayModal({ year, onClose, onSubmit }) {
  const { t } = useTranslation();
  const p = useP();
  const [dateStr, setDateStr] = useState(`${year}-01-01`);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('official');
  return (
    <Modal title={p('Ajouter un jour')} onClose={onClose}>
      <FormRow label={t('phrases:p_6b38300a25')}>
        <select value={kind} onChange={e => setKind(e.target.value)} style={modalInput}>
          {HOLIDAY_KINDS.map(k => (
            <option key={k.value} value={k.value}>{k.label}</option>
          ))}
        </select>
      </FormRow>
      <FormRow label={p('Date')}>
        <input type="date" value={dateStr}
               min={`${year}-01-01`} max={`${year}-12-31`}
               onChange={e => setDateStr(e.target.value)} style={modalInput} />
      </FormRow>
      <FormRow label={t('phrases:p_f5485bbae8')}>
        <input type="text" maxLength={120} value={label}
               onChange={e => setLabel(e.target.value)} style={modalInput} />
      </FormRow>
      <p style={modalHint}>
        {kind === 'official' && p('Férié officiel : déduit des jours ouvrés.')}
        {kind === 'offered' && p('Congé offert : se comporte comme un férié (déduit des jours ouvrés).')}
        {kind === 'compensated' && p('Congé rattrapé : déduit des jours travaillés, le volume horaire annuel ne change pas.')}
      </p>
      <ModalActions>
        <button onClick={onClose} style={btnStyle}>{t('common:actions.cancel')}</button>
        <button onClick={() => onSubmit(dateStr, label.trim(), kind)}
                disabled={!label.trim()}
                style={{ ...btnStyle, ...btnPrimary }}>{p('Ajouter')}</button>
      </ModalActions>
    </Modal>
  );
}


function NoteModal({ month, initial, onClose, onSubmit }) {
  const { t } = useTranslation();
  const p = useP();
  const [val, setVal] = useState(initial);
  return (
    <Modal title={t('phrases:p_4d57342eb5', { month: MONTH_LABELS[month - 1] })} onClose={onClose}>
      <p style={{ ...modalHint, marginTop: 0 }}>
        {p("Note libre pour ce mois (ex. dates de fermeture d'entreprise).")}
      </p>
      <textarea value={val} onChange={e => setVal(e.target.value)}
                rows={4}
                style={{ ...modalInput, width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
      <ModalActions>
        <button onClick={onClose} style={btnStyle}>{t('common:actions.cancel')}</button>
        <button onClick={() => onSubmit(val.trim())} style={{ ...btnStyle, ...btnPrimary }}>{t('common:actions.save')}</button>
      </ModalActions>
    </Modal>
  );
}


/* ════════════════════════════════════════════════════════════════════════
   UI helpers (styles inline pour rester localisé)
   ════════════════════════════════════════════════════════════════════════ */

const cardStyle = {
  background: '#fff',
  border: '1px solid #e3e8ef',
  borderRadius: 6,
  padding: '14px 16px',
};
const cardTitleStyle = { margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: '#1f3864' };
const rowStyle  = { borderBottom: '1px dotted #eee' };
const lblCellStyle = { padding: '4px 0', color: '#555', fontSize: 13 };
const valCellStyle = { padding: '4px 0', textAlign: 'right', fontSize: 13 };
const unitStyle = { marginLeft: 6, color: '#888' };

// Champs saisis par l'utilisateur — convention Palestre (bleu)
const editableInputStyle = {
  width: 80,
  padding: '2px 6px',
  textAlign: 'right',
  border: '1px solid var(--color-field-focus)',
  borderRadius: 3,
  fontSize: 13,
  background: 'var(--color-input-bg)',
  color: 'var(--color-input-text)',
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
  outline: 'none',
};
// Valeur cliquable sans cadre — texte bleu seulement, spinner natif conservé
const editableValueStyle = {
  width: 80,
  padding: '2px 6px',
  textAlign: 'right',
  border: 'none',
  background: 'transparent',
  fontSize: 13,
  color: 'var(--color-input-text)',
  fontWeight: 500,
  fontVariantNumeric: 'tabular-nums',
  outline: 'none',
};
// Valeur cliquable sans cadre — cycle entre les options possibles
const cycleValueStyle = {
  color: 'var(--color-input-text)',
  fontWeight: 500,
  cursor: 'pointer',
  userSelect: 'none',
};

const thStyle  = { padding: '6px 8px', fontWeight: 600, fontSize: 12, color: '#555', borderBottom: '1px solid #e3e8ef', textAlign: 'left' };
const thRight  = { ...thStyle, textAlign: 'right' };
const tdStyle  = { padding: '4px 8px' };
const tdRight  = { ...tdStyle, textAlign: 'right' };
const btnPrimary = { background: '#1f3864', color: '#fff', border: '1px solid #1f3864' };
const modalInput = { padding: '4px 8px', fontSize: 13, border: '1px solid #cdd5df', borderRadius: 3 };
const modalHint  = { fontSize: 12, color: '#666', margin: '8px 0' };


function Modal({ title, children, onClose }) {
  return (
    <div onClick={onClose}
         style={{
           position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
           display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
         }}>
      <div onClick={e => e.stopPropagation()}
           style={{
             background: '#fff', padding: 18, borderRadius: 6, minWidth: 380, maxWidth: 540,
             boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
           }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0' }}>
      <label style={{ width: 200, color: '#555', fontSize: 13 }}>{label} :</label>
      {children}
    </div>
  );
}

function ModalActions({ children }) {
  return <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>{children}</div>;
}


function ErrorBanner({ msg, onDismiss }) {
  return (
    <div style={{
      margin: '10px 0', padding: '8px 12px',
      background: '#fdecea', border: '1px solid #f5c2c0', borderRadius: 4,
      color: '#722', fontSize: 13,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <span>{msg}</span>
      <button onClick={onDismiss} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#722' }}>×</button>
    </div>
  );
}
